import {Controller} from "stimulus"

/**
 * Stimulus controller for expandable goal items in the FWA dashboard.
 *
 * Usage in Hiccup:
 *   [:div {:data-controller "fwa-goal"}
 *    [:button {:data-action "click->fwa-goal#toggle"
 *              :data-fwa-goal-target "arrow"}
 *     arrow-icon]
 *    [:div {:data-fwa-goal-target "content"}
 *     expanded-content]]
 *
 * Handles:
 * - Toggle expanded/collapsed state on arrow click
 * - Rotate arrow icon (down when collapsed, up when expanded)
 * - Animate content show/hide with smooth transition
 * - Update aria-expanded for accessibility
 */
export default class extends Controller {
  static get targets() {
    return ["arrow", "content"]
  }

  connect() {
    // Initialize expanded state from data attribute or default to false.
    // Apply immediately (no animation) so SSR-expanded goals don't flash.
    this.expanded = this.element.getAttribute("data-expanded") === "true"
    this.updateUI(true)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.expanded = !this.expanded
    this.updateUI()
  }

  updateUI(immediate = false) {
    const expanded = this.expanded

    // Set data-expanded first when expanding so defensive CSS (display:none when collapsed)
    // stops applying before we measure scrollHeight; otherwise scrollHeight is 0 and expand fails
    this.element.setAttribute("data-expanded", expanded.toString())

    // Update arrow rotation
    if (this.hasArrowTarget) {
      this.arrowTarget.style.transform = expanded ? "rotate(180deg)" : "rotate(0deg)"
      this.arrowTarget.style.transition = immediate ? "none" : "transform 0.2s ease-out"
      this.arrowTarget.setAttribute("aria-expanded", expanded.toString())
      this.arrowTarget.setAttribute("aria-label", expanded ? "Collapse goal details" : "Expand goal details")
    }

    // Update content visibility with animation
    if (this.hasContentTarget) {
      if (immediate) {
        // Set final state directly — used on connect so server-rendered
        // expanded goals don't animate open on page load
        this.contentTarget.style.transition = "none"
        if (expanded) {
          this.contentTarget.style.display = "block"
          this.contentTarget.style.maxHeight = "none"
          this.contentTarget.style.overflow = "visible"
          this.contentTarget.style.opacity = "1"
        } else {
          this.contentTarget.style.display = "none"
          this.contentTarget.style.maxHeight = "0px"
          this.contentTarget.style.opacity = "0"
        }
      } else if (expanded) {
        // Expand: set height to auto with animation
        this.contentTarget.style.display = "block"
        const height = this.contentTarget.scrollHeight
        this.contentTarget.style.maxHeight = "0px"
        this.contentTarget.style.overflow = "hidden"
        this.contentTarget.style.transition = "max-height 0.25s ease-out, opacity 0.2s ease-out"
        this.contentTarget.style.opacity = "0"

        // Force reflow
        this.contentTarget.offsetHeight

        // Animate to full height
        this.contentTarget.style.maxHeight = height + "px"
        this.contentTarget.style.opacity = "1"

        // After animation, remove max-height constraint
        setTimeout(() => {
          if (this.expanded) {
            this.contentTarget.style.maxHeight = "none"
            this.contentTarget.style.overflow = "visible"
          }
        }, 250)
      } else {
        // Collapse: animate to 0 then hide
        const height = this.contentTarget.scrollHeight
        this.contentTarget.style.maxHeight = height + "px"
        this.contentTarget.style.overflow = "hidden"
        this.contentTarget.style.transition = "max-height 0.2s ease-out, opacity 0.15s ease-out"

        // Force reflow
        this.contentTarget.offsetHeight

        // Animate to 0
        this.contentTarget.style.maxHeight = "0px"
        this.contentTarget.style.opacity = "0"

        // After animation, hide completely
        setTimeout(() => {
          if (!this.expanded) {
            this.contentTarget.style.display = "none"
          }
        }, 200)
      }
    }

    // Update element's data attribute for CSS hooks
    this.element.setAttribute("data-expanded", expanded.toString())
  }
}
